import { createServerFn } from "@tanstack/react-start"
import { eq } from "drizzle-orm"
import { db } from "@/db/connection"
import { guidesTable } from "@/db/schema"
import { isUuid } from "@/lib/short-id"

// SSR head() for the guide route needs the guide's header fields so it can emit
// the real banner og:image + guide title into the raw HTML — non-JS link
// crawlers (iMessage/Slack/X/Facebook) read that, not the client render.
//
// This runs *during* the server render. It must NOT make an HTTP round-trip back
// to our own /api/trpc: a self-fetch from inside the render deadlocks/fails in
// the deployment (it only succeeds from an outside connection), which silently
// dropped every guide share-link onto the default site card. So query Postgres
// directly. createServerFn keeps this handler — and the `pg` import — server-only;
// on a client-side navigation it transparently becomes a normal RPC instead.
export const fetchGuideMeta = createServerFn({ method: `GET` })
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data }) => {
    const [guide] = await db
      .select({
        short_id: guidesTable.short_id,
        name: guidesTable.name,
        description: guidesTable.description,
        image_url: guidesTable.image_url,
        destination: guidesTable.destination,
        destination_latitude: guidesTable.destination_latitude,
        destination_longitude: guidesTable.destination_longitude,
        source_trip_id: guidesTable.source_trip_id,
        published: guidesTable.published,
      })
      .from(guidesTable)
      // The route param is a short id in new links, a uuid in old ones.
      // Comparing a non-uuid string against the uuid column is a Postgres
      // cast error, so branch on shape instead of OR-ing the two.
      .where(
        isUuid(data.id)
          ? eq(guidesTable.id, data.id)
          : eq(guidesTable.short_id, data.id)
      )
      .limit(1)
    return { guide: guide ?? null }
  })
